import { PaymentDetails, PaymentError } from '@cashfreepayments/cashfree-js';
import { useToast } from '@chakra-ui/react';
import useCashfree from './useCashfree';

interface UseAddLiveCreditsProps {
  onSuccess?: (details: PaymentDetails) => void;
  onError?: (error: PaymentError | Error) => void;
  debug?: boolean;
}

interface UseAddLiveCreditsReturn {
  handleLiveCredits: (paymentSessionId: string) => Promise<void>;
}

const useAddLiveCredits = ({ onSuccess, onError, debug = false }: UseAddLiveCreditsProps): UseAddLiveCreditsReturn => {
  const toast = useToast();
  const isDevelopment = process.env.REACT_APP_ENV !== 'development';
  const { isLoading, error, initiatePayment } = useCashfree({
    debug,
    mode: isDevelopment ? 'sandbox' : 'production',
  });

  const handleLiveCredits = async (paymentSessionId: string): Promise<void> => {
    if (error) {
      toast({
        title: 'Payment System Error',
        description: 'The payment system is currently unavailable',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });

      if (onError) onError(error);
      return;
    }

    if (isLoading) {
      toast({
        title: 'Payment System Loading',
        description: 'Please wait while we initialize the payment system',
        status: 'info',
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    try {
      const result = await initiatePayment(paymentSessionId);

      if (result.error) {
        toast({
          title: 'Payment Failed',
          description: result.error.message || 'There was an issue processing your payment',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });

        if (onError) onError(result.error);
      }

      if (result.redirect) {
        toast({
          title: 'Payment Redirected',
          description: 'You are being redirected to complete payment',
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
      }

      if (result.paymentDetails) {
        toast({
          title: 'Payment Completed',
          description: `Payment status: ${result.paymentDetails.paymentStatus || 'Pending'}`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });

        if (onSuccess) onSuccess(result.paymentDetails);
      }
    } catch (err) {
      const paymentError = err instanceof Error ? err : new Error(String(err));

      toast({
        title: 'Payment Error',
        description: paymentError.message || 'Could not start the payment process',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });

      if (onError) onError(paymentError);
    }
  };

  return { handleLiveCredits };
};

export default useAddLiveCredits;
